"use client";

import { Capacitor } from "@capacitor/core";
import { getLocalityPrimaryLabel } from "@/lib/location-display";
import type { SelectedLocation } from "@/lib/locations/types";
import { createClientLogger } from "@/lib/logging/client";
import { getCurrentDevicePosition } from "@/lib/native/current-location";
import { getLocationDetails } from "@/lib/nominatim";

const logger = createClientLogger("components/location/auto-location");

export type GeoPermissionState =
  | "granted"
  | "prompt"
  | "denied"
  | "unsupported";

/**
 * Current geolocation permission state WITHOUT triggering a prompt.
 * "prompt" means asking is still possible (used by the one-time nudge);
 * "unsupported" covers missing APIs and check failures.
 */
export async function getGeolocationPermissionState(): Promise<GeoPermissionState> {
  try {
    if (Capacitor.isNativePlatform()) {
      const { Geolocation } = await import("@capacitor/geolocation");
      const status = await Geolocation.checkPermissions();
      if (
        status.location === "granted" ||
        status.coarseLocation === "granted"
      ) {
        return "granted";
      }
      if (status.location === "denied" && status.coarseLocation === "denied") {
        return "denied";
      }
      return "prompt";
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      return "unsupported";
    }
    if (!navigator.permissions?.query) {
      // No introspection API (older Safari) — geolocation itself exists, so
      // a user-initiated request would still prompt.
      return "prompt";
    }
    const status = await navigator.permissions.query({ name: "geolocation" });
    return status.state;
  } catch (error) {
    logger.warn("Geolocation permission check failed", error);
    return "unsupported";
  }
}

/**
 * True only when the OS/browser has ALREADY granted geolocation — never
 * triggers a permission prompt. Auto-detection must stay silent: users who
 * haven't granted location keep the manual picker flow untouched.
 */
export async function isGeolocationGranted(): Promise<boolean> {
  return (await getGeolocationPermissionState()) === "granted";
}

/**
 * Resolves the device's current position into a SelectedLocation with a
 * friendly area name, or null when permission isn't granted / the position
 * can't be resolved. Never prompts.
 */
export async function detectCurrentArea(): Promise<SelectedLocation | null> {
  if (!(await isGeolocationGranted())) return null;

  let position: Awaited<ReturnType<typeof getCurrentDevicePosition>>;
  try {
    position = await getCurrentDevicePosition({
      enableHighAccuracy: false,
      maximumAge: 300_000,
      timeout: 8_000,
    });
  } catch (error) {
    logger.warn("Silent current-position read failed", error);
    return null;
  }

  const { latitude, longitude } = position.coords;
  return {
    latitude,
    longitude,
    displayName: await reverseGeocodeAreaLabel(latitude, longitude),
    source: "current",
  };
}

/**
 * Friendly area name for coordinates ("Worli", "Indiranagar"), falling back
 * to "Current location" — coordinates are what the surfaces query by, the
 * label is only chrome.
 */
export async function reverseGeocodeAreaLabel(
  latitude: number,
  longitude: number,
): Promise<string> {
  try {
    const place = await getLocationDetails(latitude, longitude);
    return (
      getLocalityPrimaryLabel(place) ||
      place.display_name?.split(",")[0]?.trim() ||
      "Current location"
    );
  } catch (error) {
    logger.warn("Reverse geocode for detected location failed", error);
    return "Current location";
  }
}
